// Config Service for WITAL Frontend
// Handles loading and caching backend configuration

import { apiConfig } from './apiConfig'
import { dataStore } from '../stores/dataStore'

export interface LLMModelInfo {
  display_name: string
  full_name?: string
  // Add other properties as needed
}

export interface WeatherModelInfo {
  display_name: string
  short_name: string
  // Add other properties as needed
}

export interface AdBanner {
  id: string
  image: string
  url: string
  alt: string
  enabled: boolean
  width?: number
  height?: number
}

export interface AdsConfig {
  banners: AdBanner[]
  rotation_enabled: boolean
  rotation_interval: number
  default_banner?: string
}

export interface BackendConfig {
  llm: {
    default_model: string
    available_models: string[]
    models: Record<string, LLMModelInfo>
  }
  weather_models: {
    available_models: string[]
    models: Record<string, WeatherModelInfo>
  }
  ads?: AdsConfig
}

class ConfigService {
  private static instance: ConfigService
  private config: BackendConfig | null = null
  private loadingPromise: Promise<BackendConfig> | null = null

  private constructor() {}

  static getInstance(): ConfigService {
    if (!ConfigService.instance) {
      ConfigService.instance = new ConfigService()
    }
    return ConfigService.instance
  }

  // Load configuration from backend
  async loadConfig(): Promise<BackendConfig> {
    // If already loading, return the existing promise
    if (this.loadingPromise) {
      return this.loadingPromise
    }

    // Check if already loaded in data store
    const cachedConfig = dataStore.getConfig()
    if (cachedConfig) {
      return cachedConfig
    }

    // Start loading
    dataStore.isLoadingConfig.value = true
    this.loadingPromise = this.fetchConfig()
    
    try {
      const config = await this.loadingPromise
      // Store in central data store
      dataStore.setConfig(config)
      this.config = config
      return config
    } finally {
      this.loadingPromise = null
      dataStore.isLoadingConfig.value = false
    }
  }

  private async fetchConfig(): Promise<BackendConfig> {
    const configUrl = apiConfig.configUrl
    console.log('🔧 ConfigService: Attempting to fetch config from:', configUrl)
    console.log('🔧 ConfigService: Current hostname:', window.location.hostname)
    console.log('🔧 ConfigService: Current protocol:', window.location.protocol)
    
    try {
      const response = await fetch(configUrl)
      console.log('🔧 ConfigService: Response status:', response.status)
      console.log('🔧 ConfigService: Response headers:', Object.fromEntries(response.headers.entries()))
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error('🔧 ConfigService: Error response body:', errorText)
        throw new Error(`Failed to load config: HTTP ${response.status} - ${errorText}`)
      }
      const config = await response.json()
      console.log('🔧 ConfigService: Loaded config from backend:', config)
      return config
    } catch (error) {
      console.error('🔧 ConfigService: Network error:', error)
      throw error
    }
  }

  private getDefaultConfig(): BackendConfig {
    // Minimal fallback config - should only be used if backend is completely unavailable
    return {
      llm: {
        default_model: 'gemini-3.1-flash-lite', // This will be overridden by backend
        available_models: [], // Empty - will be populated by backend
        models: {} // Empty - will be populated by backend
      },
      weather_models: {
        available_models: [], // Empty - will be populated by backend
        models: {} // Empty - will be populated by backend
      }
    }
  }

  // Get cached config (returns null if not loaded)
  getConfig(): BackendConfig | null {
    return dataStore.getConfig()
  }

  // Get LLM display name (short name for forecast screen)
  getLLMDisplayName(modelId: string): string {
    // The id is a serviceable label, and this is called during render
    // (Forecast.vue) — throwing turned a cosmetic gap into a dead
    // component whenever config was unavailable (#186).
    return this.config?.llm.models[modelId]?.display_name || modelId
  }

  // Get LLM full name (with version info for selection screen)
  getLLMFullName(modelId: string): string {
    if (!this.config) {
      throw new Error('Config not loaded. Cannot get LLM full name.')
    }

    const model = this.config.llm.models[modelId]
    return model?.full_name || model?.display_name || modelId
  }

  // Get weather model display name
  getWeatherModelDisplayName(modelId: string): string {
    if (!this.config) {
      throw new Error('Config not loaded. Cannot get weather model display name.')
    }
    
    return this.config.weather_models.models[modelId]?.display_name || modelId
  }

  // Get weather model short name (for attribution)
  getWeatherModelShortName(modelId: string): string {
    // Same as getLLMDisplayName: degrade to the id rather than throwing
    // from a formatting helper (#186).
    return this.config?.weather_models.models[modelId]?.short_name || modelId
  }

  // Get ads configuration
  getAdsConfig(): AdsConfig | null {
    return this.config?.ads || null
  }

  // Get enabled banners
  getEnabledBanners(): AdBanner[] {
    const adsConfig = this.getAdsConfig()
    if (!adsConfig) return []
    return adsConfig.banners.filter(banner => banner.enabled)
  }

  // Get banner by ID
  getBannerById(id: string): AdBanner | null {
    const enabledBanners = this.getEnabledBanners()
    return enabledBanners.find(banner => banner.id === id) || null
  }

  // Get default banner
  getDefaultBanner(): AdBanner | null {
    const adsConfig = this.getAdsConfig()
    if (!adsConfig?.default_banner) return null
    return this.getBannerById(adsConfig.default_banner)
  }

  // Get rotation settings
  getRotationEnabled(): boolean {
    return this.config?.ads?.rotation_enabled ?? false
  }

  getRotationInterval(): number {
    return this.config?.ads?.rotation_interval ?? 30000
  }

  // Clear cached config (for testing/refresh)
  clearCache(): void {
    this.config = null
    this.loadingPromise = null
    dataStore.config.value = null
  }
}

// Export singleton instance
export const configService = ConfigService.getInstance() 